import React, { useState } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert, Tab, Tabs } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { useAtomValue } from 'jotai';
import { ChangeNameReq, ChangeEmailReq, ChangePasswordReq } from './api/Application';
import { Api } from './api/Api';
import { authAtom } from "./Atoms";

interface Status {
    saving:  boolean;
    error:   string;
    success: string;
}

const blank: Status = { saving: false, error: '', success: '' };

export function ProfilePage() {
    const auth     = useAtomValue( authAtom );
    const employee = auth.employee!;
    const [activeKey,   setActiveKey]   = useState( 'profile' );
    const [nameStatus,  setNameStatus]  = useState<Status>( blank );
    const [emailStatus, setEmailStatus] = useState<Status>( blank );
    const [pwStatus,    setPwStatus]    = useState<Status>( blank );

    const {
        register:     registerName,
        handleSubmit: handleSubmitName,
        formState:    { errors: nameErrors }
    } = useForm<ChangeNameReq>( {
        defaultValues: { name: employee.name || '' }
    } );

    const {
        register:     registerEmail,
        handleSubmit: handleSubmitEmail,
        formState:    { errors: emailErrors }
    } = useForm<ChangeEmailReq>( {
        defaultValues: { newEmailAddress: employee.emailAddress || '' }
    } );

    const {
        register:     registerPassword,
        handleSubmit: handleSubmitPassword,
        formState:    { errors: passwordErrors },
        watch:        watchPassword
    } = useForm<ChangePasswordReq & { confirmPassword: string }>();

    const newPassword = watchPassword( 'newPassword' );

    const onNameSubmit = async ( data: ChangeNameReq ) => {
        setNameStatus( { saving: true, error: '', success: '' } );
        const result = await Api.changeName( data );
        if( result.isOk ) setNameStatus( { saving: false, error: '', success: 'Your profile has been updated successfully.' } );
        else              setNameStatus( { saving: false, error: result.error, success: '' } );
    };

    const onEmailSubmit = async ( data: ChangeEmailReq ) => {
        setEmailStatus( { saving: true, error: '', success: '' } );
        const result = await Api.changeEmail( data );
        if( result.isOk ) setEmailStatus( { saving: false, error: '', success: 'Your email has been updated successfully.' } );
        else              setEmailStatus( { saving: false, error: result.error, success: '' } );
    };

    const onPasswordSubmit = async ( data: ChangePasswordReq & { confirmPassword: string } ) => {
        const { confirmPassword, ...passwordData } = data;
        setPwStatus( { saving: true, error: '', success: '' } );
        const result = await Api.changePassword( passwordData );
        if( result.isOk ) setPwStatus( { saving: false, error: '', success: 'Your password has been updated successfully.' } );
        else              setPwStatus( { saving: false, error: result.error, success: '' } );
    };

    if( !auth ) {
        return (
            <Container className="mt-5">
                <Alert variant="danger">
                    You must be logged in to view this page.
                </Alert>
            </Container>
        );
    }

    return (
        <Container className="mt-4">
            <Row className="justify-content-center">
                <Col md={8}>
                    <Card>
                        <Card.Header>
                            <h3 className="mb-0">Profile Settings</h3>
                        </Card.Header>
                        <Card.Body>
                            <Tabs
                                activeKey={activeKey}
                                onSelect={( k ) => k && setActiveKey( k )}
                                className="mb-4"
                            >
                                <Tab eventKey="profile" title="Profile Information">
                                    {nameStatus.success && <Alert variant="success">{nameStatus.success}</Alert>}
                                    {nameStatus.error   && <Alert variant="danger">{nameStatus.error}</Alert>}

                                    <Form onSubmit={handleSubmitName( onNameSubmit )}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Name</Form.Label>
                                            <Form.Control
                                                type="text"
                                                {...registerName( 'name', { required: 'Name is required' } )}
                                                isInvalid={!!nameErrors.name}
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {nameErrors.name?.message}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                        <Button variant="primary" type="submit" disabled={nameStatus.saving}>
                                            {nameStatus.saving ? 'Saving...' : 'Save Changes'}
                                        </Button>
                                    </Form>
                                </Tab>

                                <Tab eventKey="email" title="Email Address">
                                    {emailStatus.success && <Alert variant="success">{emailStatus.success}</Alert>}
                                    {emailStatus.error   && <Alert variant="danger">{emailStatus.error}</Alert>}

                                    <Form onSubmit={handleSubmitEmail( onEmailSubmit )}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Current Email</Form.Label>
                                            <Form.Control
                                                type="email"
                                                value={employee.emailAddress}
                                                disabled
                                            />
                                        </Form.Group>
                                        <Form.Group className="mb-3">
                                            <Form.Label>New Email Address</Form.Label>
                                            <Form.Control
                                                type="email"
                                                {...registerEmail( 'newEmailAddress', {
                                                    required: 'Email is required',
                                                    pattern: {
                                                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                                        message: 'Invalid email address'
                                                    }
                                                } )}
                                                isInvalid={!!emailErrors.newEmailAddress}
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {emailErrors.newEmailAddress?.message}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Current Password</Form.Label>
                                            <Form.Control
                                                type="password"
                                                {...registerEmail( 'currentPassword', { required: 'Password is required to change email' } )}
                                                isInvalid={!!emailErrors.currentPassword}
                                            />
                                            <Form.Control.Feedback type="invalid">{emailErrors.currentPassword?.message}</Form.Control.Feedback>
                                            <Form.Text className="text-muted">Enter your current password to confirm this change.</Form.Text>
                                        </Form.Group>
                                        <Button variant="primary" type="submit" disabled={emailStatus.saving}>
                                            {emailStatus.saving ? 'Updating Email...' : 'Update Email'}
                                        </Button>
                                    </Form>
                                </Tab>

                                <Tab eventKey="password" title="Change Password">
                                    {pwStatus.success && <Alert variant="success">{pwStatus.success}</Alert>}
                                    {pwStatus.error   && <Alert variant="danger">{pwStatus.error}</Alert>}

                                    <Form onSubmit={handleSubmitPassword( onPasswordSubmit )}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Current Password</Form.Label>
                                            <Form.Control
                                                type="password"
                                                {...registerPassword( 'currentPassword', {
                                                    required: 'Current password is required'
                                                } )}
                                                isInvalid={!!passwordErrors.currentPassword}
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {passwordErrors.currentPassword?.message}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                        <Form.Group className="mb-3">
                                            <Form.Label>New Password</Form.Label>
                                            <Form.Control
                                                type="password"
                                                {...registerPassword( 'newPassword', {
                                                    required: 'New password is required',
                                                    minLength: {
                                                        value: 8,
                                                        message: 'Password must be at least 8 characters'
                                                    },
                                                    pattern: {
                                                        value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,
                                                        message: 'Password must include uppercase, lowercase, number and special character'
                                                    }
                                                } )}
                                                isInvalid={!!passwordErrors.newPassword}
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {passwordErrors.newPassword?.message}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Confirm New Password</Form.Label>
                                            <Form.Control
                                                type="password"
                                                {...registerPassword( 'confirmPassword', {
                                                    validate: value =>
                                                        value === newPassword || 'Passwords do not match'
                                                } )}
                                                isInvalid={!!passwordErrors.confirmPassword}
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {passwordErrors.confirmPassword?.message}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                        <Button variant="primary" type="submit" disabled={pwStatus.saving}>
                                            {pwStatus.saving ? 'Updating Password...' : 'Update Password'}
                                        </Button>
                                    </Form>
                                </Tab>

                            </Tabs>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
}